import { snapshotEngineSettings, type EngineSettingsValues } from './constants.js';

// ─────────────────────────────────────────────────────────────────────
// ENGINE EFFECTS — the clock and the id source, as arguments.
//
// Issue #203. The result appliers used to reach for `Date.now()` and
// `Math.random()` directly. In a browser that is harmless: a click happens
// once, at one moment, and nobody replays it. Server-side (#183) neither
// holds — a retried command is ordinary, and an applier that reads the wall
// clock gives a different answer on the retry than it gave the first time.
//
// Two distinct problems hide behind "impure":
//
//   THE CLOCK CHANGES THE NUMBERS. `getPlayerKMultiplier` has an inactivity
//   branch: a player whose `lastMatchAt` is more than NEWBIE_INACTIVE_DAYS in
//   the past gets the full returning-player boost. So the SAME result, applied
//   a day later, can move the ELO further than it would have. That is not a
//   cosmetic difference; it is a different rating.
//
//   THE ID SOURCE BREAKS CONVERGENCE. A `Math.random()` suffix means a
//   retried rebuild mints new ids rather than reproducing the old ones, so an
//   interrupted-and-retried operation leaves duplicates instead of settling.
//
// Passing these in fixes both without changing a single current result: every
// default below is exactly what the code did before, so an omitted argument
// behaves identically. `getPlayerKMultiplier`, `applyEloUpdate` and
// `calcSingleMatchEloChange` already took `now` this way — the appliers simply
// never passed it down, which is how three independent clock reads ended up
// inside one result application.
// ─────────────────────────────────────────────────────────────────────

export interface EngineEffects {
  /**
   * The engine settings this operation computes against (#243).
   *
   * Defaults to a frozen snapshot of the global singleton, so an omitted
   * argument behaves exactly as before — which is what the parity suite in
   * tests/engine-settings-parity.test.ts pins.
   *
   * WHY IT IS A SNAPSHOT AND NOT THE SINGLETON. Settings are read lazily at
   * every use: one single-match application reads them 17 times, and the pad
   * command's calc-then-apply about 34. Server-side those reads are spread
   * across awaits and across replicas, so a singleton refreshed in the
   * background can change midway and produce a result computed partly with one
   * K-factor and partly with another — no error, and nothing afterwards can
   * tell. Taking the values ONCE, up front, and passing them down removes that
   * possibility rather than making it unlikely.
   */
  settings?: EngineSettingsValues;
  /**
   * The instant this operation happens, as an epoch millisecond value.
   * Defaults to `Date.now()`, read ONCE per operation.
   */
  now?: number;
  /**
   * The unique part of a generated id. Defaults to the four-digit random
   * number the engine has always used. Supply a counter to make ids
   * reproducible.
   */
  uniqueSuffix?: () => string;
}

export interface ResolvedEffects {
  now: number;
  uniqueSuffix: () => string;
  settings: Readonly<EngineSettingsValues>;
}

/** The historical suffix: `Math.floor(Math.random() * 10000)`, as a string. */
export function defaultUniqueSuffix(): string {
  return String(Math.floor(Math.random() * 10000));
}

/**
 * Fills in the defaults. Call this ONCE at the top of an operation and use the
 * result throughout, so every timestamp the operation writes names the same
 * instant. That is a real change in kind, not just in style: `applyPadel24Result`
 * previously read the clock separately for `completedAt`, for `playedAt`, for
 * each of the two recorded newbie multipliers, and again inside `applyEloUpdate`
 * for `lastMatchAt` — five reads that could straddle a millisecond boundary and
 * leave a match whose `completedAt` and `playedAt` disagree for no reason.
 */
export function resolveEffects(effects?: EngineEffects): ResolvedEffects {
  return {
    now: effects?.now ?? Date.now(),
    uniqueSuffix: effects?.uniqueSuffix ?? defaultUniqueSuffix,
    settings: effects?.settings ? Object.freeze({ ...effects.settings }) : snapshotEngineSettings(),
  };
}
